import { useEffect, useRef, useState } from "react";
import {
  Heart, Mail, MessageSquare, MessageCircle, Tag, Send, Eye, Users,
  Percent, Check, X, Clock, AlertTriangle, Trash2,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const SEGMENTS = [
  { key: "vip", label: "VIP", hint: "3+ commandes ou 300 € dépensés" },
  { key: "nouveaux", label: "Nouveaux", hint: "Inscrits depuis moins de 30 jours" },
  { key: "inactifs", label: "Inactifs", hint: "Aucun achat depuis 90 jours" },
];

const CHANNELS = [
  { key: "email", label: "Email", icon: Mail },
  { key: "sms", label: "SMS", icon: MessageSquare },
  { key: "whatsapp", label: "WhatsApp", icon: MessageCircle },
];

const STATUS_STYLES = {
  draft: "bg-stone/40 text-graphite border-stone",
  sent: "bg-green-100 text-green-800 border-green-200",
};
const STATUS_LABELS = { draft: "Brouillon", sent: "Envoyée" };

const EMPTY_FORM = {
  name: "", segment: "vip", channel: "email", subject: "", message: "",
  coupon: { enabled: false, discount_type: "percent", discount_value: 10, validity_days: 14, prefix: "PROMO" },
};

const Lbl = ({ children }) => <label className="text-xs uppercase tracking-[0.15em] text-ash">{children}</label>;

export default function SuperLoyalty() {
  const { user } = useAuth();
  const [segments, setSegments] = useState(null);
  const [campaigns, setCampaigns] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [preview, setPreview] = useState(null);
  const messageRef = useRef(null);

  const load = () => {
    api.get("/superadmin/loyalty/segments").then(({ data }) => setSegments(data)).catch(() => {});
    api.get("/superadmin/loyalty/campaigns").then(({ data }) => setCampaigns(Array.isArray(data) ? data : [])).catch(() => setCampaigns([]));
  };
  useEffect(() => { load(); }, []);

  if (user?.role !== "superadmin") return <p className="text-graphite">Accès réservé au super administrateur.</p>;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setCoupon = (k, v) => setForm((f) => ({ ...f, coupon: { ...f.coupon, [k]: v } }));

  const insertVariable = (token) => {
    const el = messageRef.current;
    if (!el) return setForm((f) => ({ ...f, message: f.message + token }));
    const start = el.selectionStart ?? form.message.length;
    const end = el.selectionEnd ?? form.message.length;
    const next = form.message.slice(0, start) + token + form.message.slice(end);
    setForm((f) => ({ ...f, message: next }));
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const openPreview = () => {
    const sampleCoupon = form.coupon.enabled ? `${(form.coupon.prefix || "PROMO").toUpperCase()}-A1B2C3` : "";
    const render = (t) => (t || "").replace("{firstName}", "Jean").replace("{couponCode}", sampleCoupon);
    setPreview({
      targetCount: segments?.[form.segment] ?? 0,
      subject: form.channel === "email" ? render(form.subject) : null,
      message: render(form.message),
    });
  };

  const createCampaign = async () => {
    if (!form.name.trim() || !form.message.trim()) {
      toast.error("Nom et message sont requis.");
      return null;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/superadmin/loyalty/campaigns", form);
      toast.success("Campagne créée en brouillon");
      setForm(EMPTY_FORM);
      load();
      return data;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const sendCampaign = async (id) => {
    if (!window.confirm("Lancer l'envoi de cette campagne maintenant ? Cette action est irréversible.")) return;
    setSendingId(id);
    try {
      const { data } = await api.post(`/superadmin/loyalty/campaigns/${id}/send`);
      const s = data.stats;
      toast.success(`${s.sent} envoyé(s), ${s.blocked} bloqué(s) (pression), ${s.failed} échec(s)`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSendingId(null);
    }
  };

  const deleteCampaign = async (id) => {
    if (!window.confirm("Supprimer ce brouillon ?")) return;
    try {
      await api.delete(`/superadmin/loyalty/campaigns/${id}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="super-loyalty">
      <p className="text-xs uppercase tracking-[0.2em] text-ash mb-2 flex items-center gap-2">
        <Heart className="h-4 w-4 text-gold" /> Super Admin
      </p>
      <h1 className="font-serif text-4xl md:text-5xl text-ink tracking-tight mb-8">Fidélité</h1>

      {/* Segments overview */}
      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        {SEGMENTS.map((s) => (
          <div key={s.key} data-testid={`segment-count-${s.key}`} className="bg-white border border-stone rounded-sm p-5">
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-[0.16em] text-ash">{s.label}</p>
              <Users className="h-4 w-4 text-copper" strokeWidth={1.5} />
            </div>
            <p className="font-serif text-3xl text-ink mt-2">{segments ? segments[s.key] : "…"}</p>
            <p className="text-xs text-ash mt-1">{s.hint}</p>
          </div>
        ))}
      </div>

      <div className="bg-sand/50 border border-stone rounded-sm p-4 mb-8 text-sm text-graphite flex items-start gap-3">
        <AlertTriangle className="h-4.5 w-4.5 text-copper shrink-0 mt-0.5" />
        <p>
          Pression marketing appliquée automatiquement : <strong className="text-ink">1 message maximum par canal et par semaine</strong>,
          et <strong className="text-ink">4 messages maximum au total par mois</strong>, par client. Les envois bloqués par cette règle
          sont journalisés mais ne comptent pas comme échecs.
        </p>
      </div>

      {/* New campaign form */}
      <div className="bg-white border border-stone rounded-sm p-6 mb-8">
        <h2 className="font-serif text-2xl text-ink mb-5">Nouvelle campagne</h2>
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <Lbl>Nom de la campagne</Lbl>
            <Input data-testid="campaign-name" value={form.name} onChange={set("name")} className="mt-1.5 rounded-sm border-stone bg-white" placeholder="Relance VIP — été 2026" />
          </div>
          <div>
            <Lbl>Segment ciblé</Lbl>
            <select data-testid="campaign-segment" value={form.segment} onChange={set("segment")} className="mt-1.5 w-full border border-stone rounded-sm p-2.5 text-sm bg-white">
              {SEGMENTS.map((s) => <option key={s.key} value={s.key}>{s.label} ({segments ? segments[s.key] : "…"} clients)</option>)}
            </select>
          </div>

          <div className="md:col-span-2 min-w-0">
            <Lbl>Canal</Lbl>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {CHANNELS.map((c) => (
                <button
                  key={c.key} type="button" data-testid={`campaign-channel-${c.key}`}
                  onClick={() => setForm((f) => ({ ...f, channel: c.key }))}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm border rounded-sm transition-colors ${
                    form.channel === c.key ? "bg-ink text-cream border-ink" : "border-stone text-graphite hover:border-ink"
                  }`}
                >
                  <c.icon className="h-4 w-4" /> {c.label}
                </button>
              ))}
            </div>
          </div>

          {form.channel === "email" && (
            <div className="md:col-span-2">
              <Lbl>Objet (email)</Lbl>
              <Input data-testid="campaign-subject" value={form.subject} onChange={set("subject")} className="mt-1.5 rounded-sm border-stone bg-white" placeholder="Une surprise pour vous, {firstName} !" />
            </div>
          )}

          <div className="md:col-span-2 min-w-0">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Lbl>Message</Lbl>
              <div className="flex flex-wrap gap-1.5">
                <button type="button" onClick={() => insertVariable("{firstName}")} className="text-[11px] uppercase tracking-wide px-2 py-1 border border-stone rounded-sm text-copper hover:bg-sand">+ {"{firstName}"}</button>
                <button type="button" onClick={() => insertVariable("{couponCode}")} className="text-[11px] uppercase tracking-wide px-2 py-1 border border-stone rounded-sm text-copper hover:bg-sand">+ {"{couponCode}"}</button>
              </div>
            </div>
            <textarea
              ref={messageRef}
              data-testid="campaign-message"
              value={form.message}
              onChange={set("message")}
              rows={5}
              className="mt-1.5 w-full border border-stone rounded-sm p-3 text-sm bg-white focus:outline-none focus:border-gold"
              placeholder={"Bonjour {firstName}, profitez de -10% avec le code {couponCode} !"}
            />
          </div>

          {/* Coupon */}
          <div className="md:col-span-2 border border-stone rounded-sm p-4">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm font-medium text-ink"><Tag className="h-4 w-4 text-copper" /> Générer un coupon</span>
              <Switch data-testid="campaign-coupon-toggle" checked={form.coupon.enabled} onCheckedChange={(v) => setCoupon("enabled", v)} className="data-[state=checked]:bg-gold" />
            </div>
            {form.coupon.enabled && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                <div>
                  <Lbl>Type</Lbl>
                  <select value={form.coupon.discount_type} onChange={(e) => setCoupon("discount_type", e.target.value)} className="mt-1.5 w-full border border-stone rounded-sm p-2 text-sm bg-white">
                    <option value="percent">Pourcentage</option>
                    <option value="amount">Montant fixe</option>
                  </select>
                </div>
                <div>
                  <Lbl>Valeur</Lbl>
                  <div className="relative mt-1.5">
                    <Input type="number" min="0" value={form.coupon.discount_value} onChange={(e) => setCoupon("discount_value", parseFloat(e.target.value) || 0)} className="rounded-sm border-stone bg-white pr-8" />
                    <Percent className="h-3.5 w-3.5 text-ash absolute right-2.5 top-1/2 -translate-y-1/2" />
                  </div>
                </div>
                <div>
                  <Lbl>Validité (jours)</Lbl>
                  <Input type="number" min="1" value={form.coupon.validity_days} onChange={(e) => setCoupon("validity_days", parseInt(e.target.value) || 1)} className="mt-1.5 rounded-sm border-stone bg-white" />
                </div>
                <div>
                  <Lbl>Préfixe</Lbl>
                  <Input value={form.coupon.prefix} onChange={(e) => setCoupon("prefix", e.target.value)} className="mt-1.5 rounded-sm border-stone bg-white" placeholder="PROMO" />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mt-6">
          <button
            type="button" data-testid="campaign-preview-btn" onClick={openPreview}
            className="flex items-center gap-2 border border-stone text-ink px-5 py-3 text-xs uppercase tracking-[0.15em] hover:border-ink transition-colors"
          >
            <Eye className="h-4 w-4" /> Aperçu
          </button>
          <button
            type="button" data-testid="campaign-create-btn" onClick={createCampaign} disabled={saving}
            className="flex items-center gap-2 bg-ink text-cream px-5 py-3 text-xs uppercase tracking-[0.15em] hover:bg-gold hover:text-ink transition-colors disabled:opacity-60"
          >
            <Send className="h-4 w-4" /> {saving ? "Création…" : "Créer la campagne"}
          </button>
        </div>
      </div>

      {/* Campaign list */}
      <h2 className="font-serif text-2xl text-ink mb-4">Campagnes</h2>
      {!campaigns ? (
        <div className="space-y-2">{[1, 2].map((i) => <div key={i} className="h-16 bg-white border border-stone animate-pulse rounded-sm" />)}</div>
      ) : campaigns.length === 0 ? (
        <p className="text-graphite">Aucune campagne pour le moment.</p>
      ) : (
        <div className="space-y-3">
          {campaigns.map((c) => {
            const ChannelIcon = CHANNELS.find((ch) => ch.key === c.channel)?.icon || Mail;
            return (
              <div key={c.id} data-testid={`campaign-row-${c.id}`} className="bg-white border border-stone rounded-sm p-4 flex flex-wrap items-center gap-4">
                <ChannelIcon className="h-5 w-5 text-copper shrink-0" strokeWidth={1.5} />
                <div className="flex-1 min-w-[180px]">
                  <p className="text-ink font-medium">{c.name}</p>
                  <p className="text-xs text-ash mt-0.5">
                    {SEGMENTS.find((s) => s.key === c.segment)?.label} · créée le {new Date(c.created_at).toLocaleDateString("fr-FR")}
                  </p>
                </div>
                {c.status === "sent" && c.stats && (
                  <div className="flex items-center gap-4 text-xs text-graphite">
                    <span className="flex items-center gap-1 text-green-700"><Check className="h-3.5 w-3.5" /> {c.stats.sent} envoyés</span>
                    <span className="flex items-center gap-1 text-amber-700"><Clock className="h-3.5 w-3.5" /> {c.stats.blocked} bloqués</span>
                    {c.stats.failed > 0 && <span className="flex items-center gap-1 text-red-600"><X className="h-3.5 w-3.5" /> {c.stats.failed} échecs</span>}
                  </div>
                )}
                <span className={`text-[11px] uppercase tracking-wide px-2.5 py-1 rounded-full border ${STATUS_STYLES[c.status]}`}>
                  {STATUS_LABELS[c.status]}
                </span>
                {c.status === "draft" && (
                  <div className="flex items-center gap-2">
                    <button
                      data-testid={`campaign-send-${c.id}`} onClick={() => sendCampaign(c.id)} disabled={sendingId === c.id}
                      className="bg-ink text-cream px-4 py-2 text-xs uppercase tracking-[0.12em] hover:bg-gold hover:text-ink transition-colors disabled:opacity-60"
                    >
                      {sendingId === c.id ? "Envoi…" : "Lancer l'envoi"}
                    </button>
                    <button data-testid={`campaign-delete-${c.id}`} onClick={() => deleteCampaign(c.id)} className="p-2 text-graphite hover:text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="bg-cream sm:max-w-md" data-testid="campaign-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-ink">Aperçu de la campagne</DialogTitle>
            <DialogDescription className="text-graphite">
              {preview?.targetCount ?? 0} client(s) dans le segment ciblé. Exemple avec « Jean ».
            </DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="space-y-3">
              {preview.subject && (
                <div className="border border-stone rounded-sm p-3 bg-white">
                  <p className="text-[11px] uppercase tracking-wide text-ash mb-1">Objet</p>
                  <p className="text-sm text-ink">{preview.subject}</p>
                </div>
              )}
              <div className="border border-stone rounded-sm p-3 bg-white">
                <p className="text-[11px] uppercase tracking-wide text-ash mb-1">Message</p>
                <p className="text-sm text-ink whitespace-pre-wrap">{preview.message}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
